import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PageMetaLoader from '../components/PageMetaLoader';
import { en, km } from './HRMSPricePage.copy';
import { useI18n } from '../i18n';

const HRMSPricePage = () => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;

  return (
    <PageSection>
      <PageMetaLoader pageUrl="products/hrms-price" />
      <div className="container">
        <HeroPanel>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <Title>{t.title}</Title>
          <Description>{t.description}</Description>
          <ButtonRow>
            <PrimaryButton to={localePath('/contact')}>{t.contactSales}</PrimaryButton>
            <SecondaryButton to={localePath('/products/hrms')}>{t.backToHrms}</SecondaryButton>
          </ButtonRow>
        </HeroPanel>

        <PricingGrid>
          {t.pricingPlans.map((plan) => (
            <PricingCard key={plan.name} $highlight={plan.highlight}>
              {plan.highlight && <PopularBadge>{t.popularBadge}</PopularBadge>}
              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>
              <PriceRow>
                <Price>{plan.price}</Price>
                <Period>{plan.period}</Period>
              </PriceRow>
              <PlanMeta>{plan.minimum}</PlanMeta>
              <PlanMeta>{plan.setup}</PlanMeta>
            </PricingCard>
          ))}
        </PricingGrid>

        <Section>
          <SectionHeader>
            <Eyebrow>{t.standardSectionEyebrow}</Eyebrow>
            <SectionTitle>{t.modulesTitle}</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {t.standardFeatureGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Eyebrow>{t.advancedSectionEyebrow}</Eyebrow>
            <SectionTitle>{t.advancedSectionTitle}</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {t.advancedFeatureGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                {group.description && <CardDescription>{group.description}</CardDescription>}
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
                {group.examples && (
                  <ExamplesBlock>
                    <ExamplesTitle>{t.examplesTitle}</ExamplesTitle>
                    <FeatureList>
                      {group.examples.map((example) => (
                        <li key={example}>{example}</li>
                      ))}
                    </FeatureList>
                  </ExamplesBlock>
                )}
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Eyebrow>{t.supportEyebrow}</Eyebrow>
            <SectionTitle>{t.standardSupportTitle}</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {t.standardSupportGroups.map((group) => (
              <FeatureCard key={group.title}>
                <CardTitle>{group.title}</CardTitle>
                <FeatureList>
                  {group.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section>
          <SupportPanel>
            <SectionHeader>
              <Eyebrow>{t.premiumEyebrow}</Eyebrow>
              <SectionTitle>{t.premiumSupportTitle}</SectionTitle>
            </SectionHeader>
            <FeatureGrid>
              {t.premiumSupportGroups.map((group) => (
                <FeatureCard key={group.title}>
                  <CardTitle>{group.title}</CardTitle>
                  <FeatureList>
                    {group.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </FeatureList>
                </FeatureCard>
              ))}
            </FeatureGrid>
          </SupportPanel>
        </Section>
      </div>
    </PageSection>
  );
};

const PageSection = styled.main`
  padding: 5.5rem 0 4.5rem;
  background: linear-gradient(180deg, #f5fbff 0%, #ffffff 64%);
`;

const HeroPanel = styled.section`
  border: 1px solid rgba(0, 142, 204, 0.16);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.05);
  padding: 2.4rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.6rem;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.9rem;
`;

const Title = styled.h1`
  color: #111827;
  font-size: 2.6rem;
  margin-bottom: 0.85rem;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  max-width: 760px;
  color: #4b5563;
  line-height: 1.7;
  font-size: 1.05rem;
  margin: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.5rem;
`;

const BaseButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-weight: 700;
  padding: 0.75rem 1.35rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: var(--primary-color);
  color: #fff;

  &:hover {
    background: #0077ab;
    box-shadow: 0 10px 24px rgba(0, 142, 204, 0.24);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: #fff;
  color: var(--primary-color);
  border: 1px solid rgba(0, 142, 204, 0.28);
`;

const PricingGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.article<{ $highlight: boolean }>`
  position: relative;
  border: 1px solid ${({ $highlight }) => ($highlight ? 'var(--primary-color)' : '#e5edf3')};
  border-radius: 16px;
  background: #fff;
  box-shadow: ${({ $highlight }) => ($highlight ? '0 16px 40px rgba(0, 142, 204, 0.12)' : 'none')};
  padding: 1.6rem;
`;

const PopularBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--primary-color);
  color: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 700;
`;

const PlanName = styled.h2`
  color: #111827;
  font-size: 1.45rem;
  margin-bottom: 0.55rem;
`;

const PlanDescription = styled.p`
  color: #5b6572;
  line-height: 1.65;
  margin: 0 0 1.1rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Price = styled.span`
  color: var(--primary-color);
  font-size: 2.7rem;
  font-weight: 800;
`;

const Period = styled.span`
  color: #4b5563;
  font-weight: 600;
`;

const PlanMeta = styled.div`
  color: #374151;
  margin-top: 0.45rem;
`;

const Section = styled.section`
  margin-top: 2.5rem;
`;

const SectionHeader = styled.div`
  margin-bottom: 1.2rem;
`;

const SectionTitle = styled.h2`
  color: #111827;
  font-size: 1.9rem;
  margin-bottom: 0;

  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  border: 1px solid #e5edf3;
  border-radius: 14px;
  background: #fff;
  padding: 1.25rem;
`;

const CardTitle = styled.h3`
  color: var(--primary-color);
  font-size: 1.12rem;
  margin-bottom: 0.65rem;
`;

const CardDescription = styled.p`
  color: #5b6572;
  line-height: 1.6;
  margin: 0 0 0.8rem;
`;

const FeatureList = styled.ul`
  display: grid;
  gap: 0.45rem;
  padding-left: 1.15rem;
  margin: 0;
  color: #374151;
  line-height: 1.55;
`;

const ExamplesBlock = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #edf2f7;
`;

const ExamplesTitle = styled.h4`
  color: #111827;
  margin-bottom: 0.55rem;
`;

const SupportPanel = styled.div`
  border-radius: 18px;
  padding: 1.6rem;
  background: linear-gradient(180deg, rgba(0, 142, 204, 0.08), rgba(255, 255, 255, 1));
  border: 1px solid rgba(0, 142, 204, 0.16);
`;

export default HRMSPricePage;
